import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { SelectDropdown, Spinner, PageTitle } from '@gsa/afp-component-library';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import queryString from 'query-string';
import useDidMountEffect from '../../../hooks/use-did-mount';
import Breadcrumbs from '../../../widgets/breadcrumbs';
import { useFvsSupportingDocs } from '../fvs-supporting-docs-provider';
import { useFederalStandards } from '../fvs-provider';
import FvsMessages from '../components/fvs-messages';
import StandardComments, {
  CommentLinkedEntityTypes,
} from '../components/standard-comments';
import { useFvsCommentsContext } from '../fvs-comments-provider';
import ConfirmComment from './confirm-comment';
import DocumentViewTabContent from '../../../components/fvs/document-view-tab-content';
import './styles/document-preview.scss';

const VehicleStandardsDocumentPreviewPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { year, fvsId, docId } = params;

  const {
    setFvsSupportingDocsData,
    getSignedURL,
    supportingDocs,
    selectedDocument,
    getStandardsSupportingDocs,
    downloadPublicFile,
  } = useFvsSupportingDocs();

  const {
    vehicleStandard,
    getVehicleStandards,
    removeFvsMessageById,
    clearAllMessages,
  } = useFederalStandards();

  const { getStandardsComments } = useFvsCommentsContext();

  const [pdfValue, setPdfValue] = useState({ value: '' });
  const [pdfOptions, setPdfOptions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { showCommentTab } = queryString.parse(location.search);

  useEffect(() => {
    clearAllMessages();
    setFvsSupportingDocsData('SET_SELECTED_DOC', null);
  }, [fvsId]);

  useEffect(() => {
    return () => {
      // Clear generated messages before unload
      clearAllMessages();
    };
  }, []);

  useEffect(() => {
    if (showCommentTab) {
      setFvsSupportingDocsData('SET_SHOW_DOC_COMMENT_TAB', true);
      setSelectedIndex(1);
    }
  }, [showCommentTab]);

  // Fetch comments when navigating with showCommentTab and vehicleStandard is loaded
  useEffect(() => {
    if (showCommentTab && vehicleStandard?.id) {
      getStandardsComments({
        variables: {
          filters: {
            operator: 'AND',
            value: [
              {
                key: 'linkedEntityType',
                operator: 'EQ',
                value: CommentLinkedEntityTypes.Document,
              },
              {
                key: 'linkedEntityId',
                operator: 'EQ',
                value: vehicleStandard.id,
              },
            ],
          },
          limit: 50,
          offset: 0,
        },
      });
    }
  }, [showCommentTab, vehicleStandard?.id]);

  const url = location.pathname.slice(0, location.pathname.lastIndexOf('/'));

  // changes url pdfId to selected pdfValue.value in dropdown
  useDidMountEffect(() => {
    if (selectedDocument && pdfValue?.value) {
      navigate(`${url}/${pdfValue.value}`, { replace: true });
    }
  }, [pdfValue?.value]);

  const breadcrumbsPath = [
    {
      location: `${window.AFP_CONFIG.appURLs.home}/home`,
      label: 'Home',
    },
    {
      location: `${window.AFP_CONFIG.appURLs.catalog}/public/fvs/vehicle-standards`,
      label: `Federal Vehicle Standards`,
    },
  ];

  useEffect(() => {
    if (fvsId && (!vehicleStandard?.id || vehicleStandard.id !== fvsId)) {
      getVehicleStandards({
        variables: {
          filters: {
            operator: 'AND',
            value: [
              {
                operator: 'EQ',
                key: 'id',
                value: fvsId,
              },
            ],
          },
          limit: 1,
          offset: 0,
          order: 'fedStandardCode ASC',
        },
      });
    }
  }, [fvsId]);

  useEffect(() => {
    if (vehicleStandard?.id) {
      getStandardsSupportingDocs({
        variables: {
          filters: {
            operator: 'AND',
            value: [
              {
                operator: 'EQ',
                key: 'fvsId',
                value: vehicleStandard.id,
              },
            ],
          },
          limit: 100,
          offset: 0,
          order: 'createdAt ASC',
        },
      });
    }
  }, [vehicleStandard?.id, fvsId]);

  // maps options for dropdown
  useEffect(() => {
    if (supportingDocs) {
      const options = supportingDocs.rows?.map((option) => ({
        value: option.id,
        label: option.metadata.name,
      }));
      setPdfOptions(options);
    }
  }, [supportingDocs]);

  // sets the active doc based on URL params
  useEffect(() => {
    if (supportingDocs?.count > 0) {
      // supportingDocs may be a partial slice left over from the landing page's
      // paginated fetch. Only fall back to the first doc once we have the
      // complete list for this fvs — otherwise wait for the limit:100 fetch.
      const hasCompleteDocList =
        supportingDocs.rows.length === supportingDocs.count;
      if (docId) {
        const activeDoc = supportingDocs.rows.find((doc) => doc.id === docId);
        if (activeDoc) {
          setPdfValue({ value: activeDoc.id });
        } else if (hasCompleteDocList) {
          // docId not found in the full list (e.g. navigated to different fvs), fall back to first
          const firstDoc = supportingDocs.rows[0];
          if (firstDoc) {
            setPdfValue({ value: firstDoc.id });
            navigate(
              `/public/fvs/vehicle-standards/preview/${year}/${fvsId}/${firstDoc.id}${location.search}`,
              { replace: true },
            );
          }
        }
      } else if (hasCompleteDocList) {
        const activeDoc = supportingDocs.rows[0];
        if (activeDoc) {
          setPdfValue({ value: activeDoc?.id });
          navigate(
            `/public/fvs/vehicle-standards/preview/${year}/${fvsId}/${activeDoc?.id}${location.search}`,
            { replace: true },
          );
        }
      }
    }
  }, [supportingDocs, docId]);

  // changes active doc based on selected dropdown option
  useEffect(() => {
    if (supportingDocs?.count > 0) {
      const selectedPdf = supportingDocs.rows.filter((doc) => {
        if (doc.id === pdfValue.value) return doc;
        return null;
      });
      setFvsSupportingDocsData('SET_SELECTED_DOC', selectedPdf[0]);
    }
  }, [supportingDocs, pdfValue.value]);

  useEffect(() => {
    if (selectedDocument) {
      getSignedURL({
        variables: {
          fileKey: selectedDocument?.metadata?.fileLocation,
          fileType: selectedDocument?.metadata?.fileMimeType,
        },
      });
    }
  }, [selectedDocument]);

  const onPdfChange = (ev) => {
    setPdfValue({ value: ev.target.value });
  };

  const download = (attachment) => {
    if (attachment?.metadata?.fileLocation) {
      downloadPublicFile({
        fileKey: attachment?.readUri,
        fileType: attachment?.metadata?.fileMimeType,
        fileName: attachment?.metadata?.name,
      });
    }
  };

  if (!selectedDocument || !supportingDocs)
    return <Spinner size="medium" className="margin-top-8" />;

  return (
    <>
      <Breadcrumbs
        data-testId="doc-preview-crumbs"
        current="Documentation"
        path={breadcrumbsPath}
      />
      <FvsMessages onClose={removeFvsMessageById} />
      <div className=" margin-bottom-15">
        <ConfirmComment tokenName="documentCommentToken" />

        <div className="display-flex flex-justify document-preview-mobile">
          <PageTitle
            className="font-sans-2xl margin-bottom-0"
            tabIndex="0"
            aria-label="Federal Standards Documentation"
            title="Federal Standards Documentation"
          />
          <SelectDropdown
            autoFocus
            label={<strong>Select a document to review</strong>}
            name="select document"
            className="document-preview-select"
            onChange={onPdfChange}
            value={pdfValue?.value}
            options={pdfOptions}
          />
        </div>
        <h2 className="text-normal margin-top-0 margin-bottom-4">
          {selectedDocument?.metadata?.name.split('.')[0]}
        </h2>

        <p className="text-primary text-bold text-uppercase margin-bottom-neg-1">
          Description
        </p>
        <p>{selectedDocument?.metadata?.description}</p>
        <br />
        <Tabs
          selectedIndex={selectedIndex}
          onSelect={(tabIndex) => setSelectedIndex(tabIndex)}
        >
          <TabList>
            <Tab data-testid="vehicle-standards-docs-tab">
              <span className="text-no-wrap">Document view</span>
            </Tab>
            <Tab data-testid="vehicle-standards-docs-tab">
              <span className="text-no-wrap">Comments</span>
            </Tab>
          </TabList>

          <TabPanel>
            <DocumentViewTabContent
              download={download}
              useFvsSupportingDocs={useFvsSupportingDocs}
            />
          </TabPanel>
          <TabPanel>
            <StandardComments
              linkedEntityType={CommentLinkedEntityTypes.Document}
            />
          </TabPanel>
        </Tabs>
      </div>
    </>
  );
};

VehicleStandardsDocumentPreviewPage.propTypes = {};

export default VehicleStandardsDocumentPreviewPage;
